import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClaimLink } from '@services/partnerService';
import { TextInputV4 } from '@components/v4/Input/Input';
import { usePartnerAuth } from '../PartnerAuthContext';
import { Button, C, Card, ErrorNote, Muted, Title } from '../ui';
import { CommerceItem } from '../components';
import { useCommerces } from '../useCommerces';

type Filter = 'todos' | 'draft' | 'reclamado';

const CommerceList = () => {
  const navigate = useNavigate();
  const { token } = usePartnerAuth();
  const { commerces, loading, error } = useCommerces();
  const [filter, setFilter] = useState<Filter>('todos');
  const [search, setSearch] = useState('');

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commerces.filter((c) => {
      if (filter !== 'todos' && c.estado !== filter) return false;
      if (!q) return true;
      const haystack = [c.negocio_nombre, c.owner_nombre, c.owner_correo, c.owner_telefono]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [commerces, filter, search]);

  // Filtro segmentado con pill chips (radius 9999) — patrón canónico (MobileOrdersViewV2):
  // activo = teal sólido / blanco; inactivo = blanco con borde grayLight.
  const tab = (key: Filter, label: string) => {
    const isActive = filter === key;
    return (
      <button
        type="button"
        onClick={() => setFilter(key)}
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: 13,
          fontWeight: isActive ? 600 : 500,
          color: isActive ? C.white : C.primary,
          background: isActive ? C.secondary : C.white,
          border: `1px solid ${isActive ? C.secondary : C.cardBorder}`,
          borderRadius: 9999,
          padding: '7px 16px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <Title>Comercios</Title>
        <Button onClick={() => navigate('/partner/comercios/nuevo')}>Nuevo comercio</Button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <TextInputV4
          value={search}
          onChange={(v) => setSearch(String(v))}
          placeholder="Buscar por negocio, dueño, correo o teléfono..."
          aria-label="Buscar comercios"
        />
      </div>

      {/* Strip de filtros: scroll horizontal en pantallas angostas (sin wrap apilado), sin
          scrollbar visible. `pr-1` lógico vía paddingRight evita que el último chip se recorte. */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 14,
          overflowX: 'auto',
          paddingBottom: 2,
          paddingRight: 4,
          scrollbarWidth: 'none',
        }}
      >
        {tab('todos', `Todos (${commerces.length})`)}
        {tab('draft', `Por activar (${commerces.filter((c) => c.estado === 'draft').length})`)}
        {tab('reclamado', `Activados (${commerces.filter((c) => c.estado === 'reclamado').length})`)}
      </div>

      <Card>
        {loading && <Muted>Cargando...</Muted>}
        {error && <ErrorNote>{error}</ErrorNote>}
        {!loading && shown.length === 0 && <Muted>No hay comercios en esta vista.</Muted>}
        {shown.map((c, i) => (
          <CommerceItem
            key={c.id_suscripcion}
            c={c}
            first={i === 0}
            quick={{
              onView: () => c.id_comercio && navigate(`/partner/comercios/${c.id_comercio}`),
              onEditContact: () => c.id_comercio && navigate(`/partner/comercios/${c.id_comercio}?edit=1`),
              onConfigure: () => c.id_comercio && navigate(`/partner/draft/${c.id_comercio}/configurar`),
              onLoadMenu: () => c.id_comercio && navigate(`/partner/draft/${c.id_comercio}/configurar?step=menu`),
              onClaimLink: async () => {
                if (!token || !c.id_comercio) throw new Error('missing token/id');
                const res = await createClaimLink(token, c.id_comercio);
                return `${window.location.origin}/pre-registro/${res.token}`;
              },
            }}
          />
        ))}
      </Card>
    </>
  );
};

export default CommerceList;
